/**
 * @lines98/client/lib/asset-protect — ЛЁГКАЯ защита игровых PNG/арт-ассетов от
 * КАЗУАЛЬНОГО «Сохранить картинку как…» / drag-and-drop / выделения (тикет
 * fe-png-protect, frontend-lead 2026-06-21, Вариант 1 — owner-решение).
 *
 * ЗАЧЕМ / WHY THIS EXISTS (owner-truth, цитата из тикета — обязательна в шапке):
 *   «Полностью запретить скачивание картинки в браузере НЕВОЗМОЖНО. Как только
 *   изображение отрисовано — оно видно, лежит в КЭШе браузера, доступно через
 *   DevTools/Network и через СКРИНШОТ ОС.» Поэтому цель модуля — отсечь ТОЛЬКО
 *   «обывательский» способ (правый клик → Сохранить, drag-and-drop на десктоп,
 *   выделение по картинке), а НЕ закрыть доступ к битмапу. Любое обещание «нельзя
 *   скачать» — вне scope.
 *
 * SCOPE (что делает):
 *   - ОДИН делегированный listener на `document` для `contextmenu` и `dragstart`:
 *     если цель события находится внутри защищаемого узла (по маркеру), вызывает
 *     `preventDefault()`. Делегирование → покрывает ЛЮБОЙ динамически примонтированный
 *     экран (мастерская / гномы / лендинг / рейл) без повторного навешивания.
 *   - `attachAssetProtection(root)` / `protectImagesIn(root)` — простановка
 *     `draggable="false"` + маркер-атрибута на защищаемых `<img>`/`background`-узлах
 *     внутри уже отрендеренного DOM (паттерн attach-не-rerender, code-structure §3).
 *     Идемпотентно (повторный вызов на том же узле listener'ы/атрибуты не плодит).
 *
 * NOT IN SCOPE (физически невозможно или отдельная фича — НЕ обещаем):
 *   watermark, серверная подпись/обфускация/шифрование ассетов, блокировка
 *   DevTools/Network, блокировка скриншотов ОС/PrintScreen. CSS-сторона защиты
 *   (`user-select`/`user-drag`/`touch-callout`) — ОДНО правило в `styles/global.css`
 *   по тому же маркеру (этот модуль её НЕ дублирует).
 *
 * 🔴 AC-2 БЛОКЕР: модуль НИКОГДА не ставит `pointer-events:none` и не глушит клик/тап.
 *   `contextmenu`/`dragstart` preventDefault гасят ТОЛЬКО контекст-меню и drag — обычный
 *   клик/тап по доске/клеткам/кнопкам проходит насквозь. Геймплей-ввод не трогаем.
 *
 * AC-9: модуль НЕ импортирует и не трогает pipeline предзагрузки/прогрева ассетов —
 *   декод изолирован (preventDefault на contextmenu/dragstart не пересекается с
 *   `img.decode()`; `img.decoding`/`img.src` мы не меняем).
 *
 * Cross-references:
 *   - docs/orchestrator/tickets/fe-png-protect.md AC-1..AC-9 (постановка).
 *   - components/meta-icons.ts (единый источник PNG-глифов; маркер `.meta-glyph`).
 *   - styles/global.css (ОДНО CSS-правило защиты по маркеру, рядом с img.meta-glyph).
 *   - code-structure §1–§3/§6 (единый переиспользуемый модуль, attach-паттерн, шапка).
 */

/**
 * Единый класс-маркер защищаемого изображения (AP-2). Тег-агностичен: один и тот же
 * `.meta-glyph` несёт КАЖДЫЙ PNG-глиф меты — и `<img>` (звери/камни/монета/осколок/
 * гномы/full-арт), и `<span class="meta-glyph meta-glyph--gem-empty">` (CSS-маска
 * пустого гнезда, канал background/mask). Поэтому правило защиты по `.meta-glyph`
 * покрывает оба канала рендера (AC-5) одним селектором.
 */
const MARKER_CLASS = 'meta-glyph';

/**
 * Дополнительные селекторы защищаемых изображений вне `.meta-glyph` (AP-2). Это
 * визуальные ассеты, которые НЕ проходят через `meta-icons.ts`:
 *   - `.mode-card-thumb-img` — статичные PNG-превью режимов на лендинге (ранее без
 *     защиты, AC-5а);
 *   - `.brand-logo` — бренд-лого (inline SVG, визуальный ассет).
 * Эти классы попадают и под делегированный listener, и под единое CSS-правило.
 */
const EXTRA_PROTECT_SELECTORS = ['.mode-card-thumb-img', '.brand-logo'] as const;

/**
 * Полный CSS-селектор всех защищаемых узлов (для делегированного `closest` и для
 * простановки атрибутов). ОДИН источник правды на «что защищаем» — потребители (CSS,
 * экраны) сверяются с этим списком, а не дублируют строки.
 */
export const PROTECT_SELECTOR = `.${MARKER_CLASS}, ${EXTRA_PROTECT_SELECTORS.join(', ')}`;

/** Маркер-атрибут на узле, на котором уже проставлены атрибуты защиты (идемпотентность). */
const ATTACHED_ATTR = 'data-asset-protect';

/** Флаг «делегированный document-listener уже установлен» — ставится один раз. */
let documentListenerInstalled = false;

/**
 * Цель события защищена? — true, если сам узел или любой его предок матчит
 * `PROTECT_SELECTOR`. Используется делегированным listener'ом.
 */
function isProtectedTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  return target.closest(PROTECT_SELECTOR) !== null;
}

/**
 * Установить ОДИН делегированный перехват `contextmenu` + `dragstart` на `document`
 * (AP-4). Вызывается один раз на boot (`installAssetProtection`); повторные вызовы —
 * no-op (флаг `documentListenerInstalled`). Делегирование выбрано вместо per-img,
 * чтобы динамически примонтированные экраны (мастерская/гномы/лендинг) были покрыты
 * без повторного навешивания, а listener'ы НЕ накапливались (идемпотентность AC-6).
 *
 * preventDefault зовётся ТОЛЬКО когда событие пришло из защищаемого узла → клик/тап
 * по доске/кнопкам не затрагивается (AC-2).
 */
function installDelegatedListeners(): void {
  if (documentListenerInstalled || typeof document === 'undefined') return;
  documentListenerInstalled = true;

  document.addEventListener(
    'contextmenu',
    (ev) => {
      if (isProtectedTarget(ev.target)) ev.preventDefault();
    },
    // НЕ capture, НЕ passive: нужен preventDefault на bubble-фазе целевого узла.
    false,
  );

  document.addEventListener(
    'dragstart',
    (ev) => {
      if (isProtectedTarget(ev.target)) ev.preventDefault();
    },
    false,
  );
}

/**
 * Проставить атрибуты защиты на УЖЕ отрендеренные защищаемые узлы внутри `root`
 * (паттерн attach-не-rerender, code-structure §3): `draggable="false"` (страховка для
 * `<img>`, где нативный drag иначе активен) + маркер-атрибут `data-asset-protect`
 * (идемпотентность: повторный вызов узлы с маркером пропускает). CSS-сторона
 * (`user-select`/`user-drag`/`touch-callout`) живёт в global.css по `.meta-glyph` —
 * сюда не дублируется.
 *
 * Идемпотентно: узлы с уже выставленным `data-asset-protect` пропускаются; сам
 * `document`-listener ставится через `installDelegatedListeners` (тоже один раз).
 *
 * @param root корневой узел поддерева, в котором искать защищаемые изображения.
 */
export function attachAssetProtection(root: ParentNode | null | undefined): void {
  // Делегированный document-перехват — единая точка для contextmenu/dragstart на ВСЁ
  // приложение. Ставится один раз (no-op при повторе), даже если root частичный.
  installDelegatedListeners();

  if (!root || typeof (root as ParentNode).querySelectorAll !== 'function') return;

  const nodes = (root as ParentNode).querySelectorAll(PROTECT_SELECTOR);
  for (const node of Array.from(nodes)) {
    if (node.hasAttribute(ATTACHED_ATTR)) continue; // идемпотентность
    node.setAttribute(ATTACHED_ATTR, '');
    // draggable=false страхует нативный drag <img> (для span-маски атрибут безвреден).
    if (node instanceof HTMLImageElement) {
      node.draggable = false;
    } else {
      node.setAttribute('draggable', 'false');
    }
  }
}

/**
 * Алиас `attachAssetProtection` под именем, отражающим «защити изображения в поддереве».
 * Один и тот же helper — два читаемых имени у точки вызова (экран / boot).
 */
export function protectImagesIn(root: ParentNode | null | undefined): void {
  attachAssetProtection(root);
}

/**
 * Установить защиту на уровне всего приложения (вызывается один раз на boot из main.ts):
 * делегированный document-перехват + первичная простановка атрибутов на текущем DOM.
 * Идемпотентно. Динамически смонтированные позже экраны покрыты делегированным
 * listener'ом автоматически; для атрибута `draggable` на новых узлах экран зовёт
 * `attachAssetProtection(root)` после mount (или полагается на per-img `draggable` из
 * meta-icons.ts, который уже зашит в разметку глифов).
 */
export function installAssetProtection(): void {
  installDelegatedListeners();
  if (typeof document !== 'undefined') {
    attachAssetProtection(document.body);
  }
}

// Тест-хук идемпотентности (AC-6): viewport-тест дёргает attachAssetProtection дважды
// и проверяет, что поведение не двоится. Выставляется только в браузере; в Node-юните
// не нужен. Безвреден для прода (косметический модуль, без секретов).
if (typeof window !== 'undefined') {
  (window as unknown as { __assetProtect?: unknown }).__assetProtect = {
    attachAssetProtection,
    protectImagesIn,
    installAssetProtection,
    PROTECT_SELECTOR,
  };
}
